import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Sparkles, Loader2, ArrowUp, Wand2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

export default function Copilot() {
  const [query, setQuery] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get("/copilot/suggestions").then((r) => setSuggestions(r.data.suggestions || []));
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const ask = async (q) => {
    const text = (q ?? query).trim();
    if (!text || loading) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setQuery("");
    setLoading(true);
    try {
      const r = await api.post("/copilot/ask", { query: text, session_id: sessionId });
      setSessionId(r.data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: r.data.answer, context_used: r.data.context_used }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: `❌ ${e?.response?.data?.detail || "Copilot failed"}`, error: true },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-72px)] flex flex-col" data-testid="copilot-page">
      {/* Header */}
      <div className="px-8 pt-8 pb-4 border-b border-white/5">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center"
               style={{ background: "linear-gradient(135deg, rgba(212,175,55,0.18), rgba(0,240,255,0.10))", border: "1px solid rgba(212,175,55,0.30)" }}>
            <Sparkles className="w-4 h-4" style={{ color: "var(--gold-400)" }} strokeWidth={1.5} />
          </div>
          <div>
            <div className="label-eyebrow">// Nexus Operating Copilot</div>
            <h1 className="font-display text-2xl text-white tracking-tight">Ask Nexus anything</h1>
          </div>
        </div>
        <p className="text-slate-400 text-sm max-w-2xl">
          Conversational executive intelligence. Powered by Claude Sonnet 4.5 + your live institutional data.
        </p>
      </div>

      {/* Conversation area */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-8 py-6 space-y-5" data-testid="copilot-conversation">
        {messages.length === 0 && (
          <div className="max-w-3xl mx-auto">
            <div className="label-eyebrow mb-3">// Suggested prompts</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {suggestions.map((s) => (
                <button
                  key={s}
                  onClick={() => ask(s)}
                  data-testid={`copilot-suggestion-${s.slice(0, 20).replace(/\s+/g, "-").toLowerCase()}`}
                  className="text-left p-4 border border-white/10 hover:border-[#D4AF37]/40 hover:bg-white/[0.03] transition-all group"
                >
                  <Wand2 className="w-3 h-3 mb-2 text-slate-500 group-hover:text-[#D4AF37]" strokeWidth={1.6} />
                  <div className="text-sm text-slate-200 group-hover:text-white">{s}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        <AnimatePresence initial={false}>
          {messages.map((m, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className={`max-w-3xl ${m.role === "user" ? "ml-auto" : "mr-auto"}`}
            >
              {m.role === "user" ? (
                <div className="ml-auto inline-block px-4 py-2.5 bg-[#D4AF37]/12 border border-[#D4AF37]/30 text-white text-sm font-body" data-testid="copilot-user-msg">
                  {m.content}
                </div>
              ) : (
                <div className="nexus-glass-panel p-5" data-testid="copilot-assistant-msg">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-3 h-3" style={{ color: "var(--gold-400)" }} strokeWidth={1.5} />
                    <span className="label-eyebrow">Nexus Copilot</span>
                    {m.context_used && m.context_used.length > 0 && (
                      <span className="font-mono text-[9px] tracking-widest text-slate-500 ml-auto">
                        {m.context_used.join(" · ").toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="nexus-prose text-sm">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </AnimatePresence>

        {loading && (
          <div className="max-w-3xl mr-auto">
            <div className="nexus-glass-panel p-5 flex items-center gap-3">
              <Loader2 className="w-4 h-4 animate-spin" style={{ color: "var(--gold-400)" }} />
              <span className="text-sm text-slate-400 font-mono tracking-widest">SYNTHESIZING…</span>
            </div>
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="px-8 pb-8 pt-3 border-t border-white/5">
        <div className="max-w-3xl mx-auto relative">
          <textarea
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                ask();
              }
            }}
            placeholder="Ask anything — pipeline, risks, government engagements, briefings, recommendations…"
            rows={2}
            data-testid="copilot-input"
            className="w-full bg-[#0A111F] border border-white/10 text-white text-sm font-body p-4 pr-14 resize-none focus:border-[#D4AF37]/60 focus:outline-none focus:ring-1 focus:ring-[#D4AF37]/30 transition-all"
          />
          <button
            onClick={() => ask()}
            disabled={loading || !query.trim()}
            data-testid="copilot-send"
            className="absolute right-3 bottom-3 w-9 h-9 flex items-center justify-center disabled:opacity-40 transition-all hover:scale-105"
            style={{ background: "var(--gold-500)", color: "#050B14" }}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUp className="w-4 h-4" strokeWidth={2.2} />}
          </button>
        </div>
        <div className="max-w-3xl mx-auto mt-2 font-mono text-[10px] tracking-widest text-slate-600 text-center">
          ENTER to send · SHIFT+ENTER for newline · NEXUS COPILOT KNOWS YOUR LIVE INSTITUTIONAL DATA
        </div>
      </div>
    </div>
  );
}
